// ===================== Screens / menus (DOM overlays) =====================
import { CharacterDefs, Elements, getWeapon, EnemyDefs, BossDefs } from './registries.js';
import { getStage } from './stages.js';
import {
  MAP_NODES, MAP_REGIONS, mapLayout, mapLinks, mapProgress, regionProgress,
  nodeState, gateReason, isConquered, isContested, isHeld, nodeById, regionById,
  shrineStatus, msToNextSiege, reclaimBounty, runConfigFor, MAX_CONTESTED,
  threatOf, threatCeiling, clampThreat, threatLabel, threatBounty, MAX_THREAT,
  anomaliesForToday, anomalyFor, msToAnomalyReset,
  gauntletUnlocked, gauntletConfigFor, formatSeed,
} from '../content/worldmap.js';
import { hexPoints } from '../engine/hex.js';
import { spriteDataUri } from '../content/sprites.js';
import { Save, decodeExport } from '../engine/save.js';
import { Settings } from '../engine/settings.js';
import { Audio } from '../engine/audio.js';
import {
  SKILL_TREE, META_BY_ID, nextCost, nodeUnlocked, PRESTIGE_BONUS, ascendPreserving,
  cheapestBuy, buyMaxPlan, ascensionCost,
} from '../content/meta.js';
import { ACHIEVEMENTS, nextHunts } from '../content/achievements.js';
import { Platform } from '../engine/platform.js';

// Same touch check the input engine uses (src/engine/input.js) — on touch
// devices the copy leads with touch controls, not the keyboard.
function isTouchDevice() {
  return ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
}

function fmtTime(s) {
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return String(m).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
}

// Coarse "3h 12m" / "42m" / "any moment" for the incursion clock.
function fmtCountdown(ms) {
  if (ms === null) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'any moment';
  if (mins < 60) return mins + 'm';
  return Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
}

function fmtDmg(n) {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return String(Math.round(n));
}

// Game-over "damage dealt" section from the run summary's top weapons.
function topWeaponsHtml(summary) {
  const top = summary.topWeapons || [];
  if (!top.length) return '';
  const rows = top.map((t) => {
    const def = getWeapon(t.id);
    return { icon: def ? def.icon : '◆', name: def ? def.name : t.id, dmg: t.dmg, pct: t.pct };
  });
  return `<div class="hint" style="margin:2px 0 4px">Damage dealt — ${fmtDmg(summary.totalDamage || 0)} total</div>${dmgRows(rows)}`;
}

// Compact per-weapon damage bars (pause + game-over panels).
function dmgRows(list) {
  if (!list || !list.length) return '';
  return `<div class="dmg-list">${list.map((r) => `
    <div class="dmg-row">
      <span class="dmg-icon">${r.icon}</span>
      <span class="dmg-name">${r.name}</span>
      <div class="dmg-bar"><div style="width:${Math.max(3, Math.round(r.pct * 100))}%"></div></div>
      <span class="dmg-val">${fmtDmg(r.dmg)}</span>
    </div>`).join('')}</div>`;
}

export class Screens {
  constructor() {
    this.root = document.getElementById('overlay');
    // Rotating / resizing changes what fits — recheck the scroll fades.
    window.addEventListener('resize', () => this._attachCues());
  }
  // opts.scenic keeps the overlay dim light so the animated backdrop shows through.
  _show(html, opts = {}) {
    this.root.innerHTML = html;
    this.root.classList.toggle('scenic', !!opts.scenic);
    this.root.classList.add('show');
    // The toast column repositions while a panel is open (see #toast-wrap) —
    // on phones its in-run slot lands square on a results panel's numbers.
    document.body.classList.add('overlay-open');
    this.root.scrollTop = 0;
    this._attachCues();
  }
  // Scroll affordance: every scrollable region shows a bottom fade while
  // content is cut off below it. Panels have shipped with primary buttons
  // silently below the fold and nothing signalling that the panel scrolls.
  _attachCues() {
    this.root.querySelectorAll('.panel, .pin-scroll, .ach-list, .md-scroll').forEach((el) => {
      if (!el._cue) {
        // "More below" means real content is cut off — not merely that the
        // bottom padding has scrolled out of view.
        el._cue = () => {
          const last = el.lastElementChild;
          const cut = !!last &&
            last.getBoundingClientRect().bottom - el.getBoundingClientRect().bottom > 2;
          el.classList.toggle('has-more', cut);
        };
        el.addEventListener('scroll', el._cue, { passive: true });
      }
      el._cue();
      requestAnimationFrame(el._cue);    // re-check once layout has settled
    });
  }
  hide() {
    this.root.classList.remove('show');
    this.root.classList.remove('scenic');
    document.body.classList.remove('overlay-open');
    this.root.innerHTML = '';
  }
  get isOpen() { return this.root.classList.contains('show'); }
  _click(sel, fn) {
    const el = this.root.querySelector(sel);
    if (el) el.addEventListener('click', (e) => { Audio.ui(); fn(e); });
  }

  // Branded yes/no dialog — replaces native confirm() in the play flow, which
  // is unstylable and jarring on mobile. Rendered as an ordinary overlay
  // panel; the caller re-renders whatever screen it came from on cancel.
  confirmDialog({ title, body, okLabel = 'OK', cancelLabel = 'Cancel', onOk, onCancel }) {
    this._show(`
      <div class="panel" style="text-align:center;max-width:420px">
        <div class="section-title">${title}</div>
        <p class="hint" style="margin-top:0">${body}</p>
        <div class="btn-row">
          <button class="btn primary" id="btn-confirm-ok">${okLabel}</button>
          <button class="btn ghost" id="btn-confirm-cancel">${cancelLabel}</button>
        </div>
      </div>`);
    this._click('#btn-confirm-ok', onOk);
    this._click('#btn-confirm-cancel', onCancel);
  }

  // ---------------------------------------------------------------- loading
  loading(pct) {
    this._show(`
      <div class="panel" style="text-align:center;max-width:420px">
        <img class="title-wordmark" style="width:min(300px,70vw)" src="${spriteDataUri('wordmark_neonfang')}" alt="NEONFANG">
        <div class="subtitle">loading…</div>
        <div id="load-bar" style="height:12px;background:#101430;border:2px solid var(--panel-edge);border-radius:6px;overflow:hidden">
          <div id="load-fill" style="height:100%;width:${Math.round((pct || 0) * 100)}%;background:linear-gradient(90deg,#2b6cff,var(--accent));transition:width .15s"></div>
        </div>
      </div>`);
  }
  setLoading(pct) {
    const f = this.root.querySelector('#load-fill');
    if (f) f.style.width = Math.round(pct * 100) + '%';
  }

  // ---------------------------------------------------------------- title
  title({ onPlay, onShop, onSettings, onAchievements, onReset }) {
    const d = Save.data;
    this._show(`
      <div class="panel" style="text-align:center; max-width:640px">
        <img class="title-emblem" src="${spriteDataUri('emblem_fang')}" alt="">
        <img class="title-wordmark" src="${spriteDataUri('wordmark_neonfang')}" alt="NEONFANG">
        <div class="subtitle">✦ BULLET HEAVEN ✦</div>
        <div class="fang-divider"></div>
        <p class="hint">
          Conquer <b style="color:var(--accent)">THE FANGLANDS</b> one hex at a time. Collect
          weapons, adopt an element, evolve your arsenal, and stack
          <b class="tag-multiply">Multiplication</b> until the screen drowns in your fire.
        </p>
        <div class="summary-grid" style="max-width:380px;margin:12px auto">
          <span class="k">◈ Coins</span><span class="v">${d.coins}${Save.ownsIap('neonfang.supporter') ? ' <span title="Supporter" style="color:var(--gold)">💛</span>' : ''}</span>
          <span class="k">Best Time</span><span class="v">${fmtTime(d.bestTime || 0)}</span>
          <span class="k">Best Kills</span><span class="v">${d.bestKills}</span>
          <span class="k">Runs</span><span class="v">${d.runs}</span>
          ${d.prestige ? `<span class="k">☄ Ascension</span><span class="v" style="color:var(--gold)">${d.prestige}</span>` : ''}
        </div>
        <div class="btn-row">
          <button class="btn primary" id="btn-play">▶ PLAY</button>
          <button class="btn" id="btn-shop">❖ Fang Tree</button>
          <button class="btn" id="btn-ach">🏆 Achievements</button>
          <button class="btn" id="btn-settings">⚙ Settings</button>
        </div>
        <p class="hint">
          ${isTouchDevice()
    ? 'Move: <b style="color:var(--accent)">touch &amp; drag anywhere</b> &nbsp; Pause: ⏸<br>'
    : 'Move: <kbd>WASD</kbd> / <kbd>◄▲▼►</kbd> / 🎮 stick &nbsp; Pause: <kbd>Esc</kbd> &nbsp; Mute: <kbd>M</kbd><br>'}
          Weapons fire automatically — you dodge, position, and build.
        </p>
        <div class="btn-row">
          <button class="btn ghost" id="btn-reset" style="font-size:12px;padding:6px 12px">Reset Save</button>
        </div>
      </div>`, { scenic: true });
    this._click('#btn-play', onPlay);
    this._click('#btn-shop', onShop);
    this._click('#btn-ach', onAchievements);
    this._click('#btn-settings', onSettings);
    this._click('#btn-reset', () => { if (confirm('Erase all progress, unlocks and upgrades?')) onReset(); });
  }

  // ------------------------------------------------------------- skill tree
  // The Fang Tree: three branches (FANG offense, HIDE defense, FORTUNE
  // economy) converge on the Apex Predator. Owning the apex unlocks ASCENSION.
  skillTree(opts, selectedId = null) {
    const { onBack, onAscended } = opts;
    const d = Save.data;
    const prestige = d.prestige || 0;
    const apexOwned = Save.metaLevel('apexpredator') >= 1;

    // Edges from prerequisite pairs; an edge lights up once its requirement is met.
    let lines = '';
    for (const node of SKILL_TREE) {
      for (const [reqId, reqLvl] of node.requires) {
        const from = META_BY_ID[reqId];
        if (!from) continue;
        const lit = Save.metaLevel(reqId) >= reqLvl;
        lines += `<line x1="${from.pos[0]}" y1="${from.pos[1]}" x2="${node.pos[0]}" y2="${node.pos[1]}"
          class="edge ${lit ? 'lit' : ''} eb-${node.branch}" vector-effect="non-scaling-stroke"/>`;
      }
    }

    const nodes = SKILL_TREE.map((u) => {
      const lvl = Save.metaLevel(u.id);
      const state = !nodeUnlocked(u) ? 'locked' : lvl >= u.max ? 'maxed' : 'avail';
      return `
        <button class="skill-node b-${u.branch} ${state} ${u.keystone ? 'keystone' : ''} ${u.apex ? 'apex' : ''} ${selectedId === u.id ? 'selected' : ''}"
                data-id="${u.id}" style="left:${u.pos[0]}%;top:${u.pos[1]}%" aria-label="${u.name}">
          <span class="sn-icon">${u.icon}</span>
          <span class="sn-lvl">${Number.isFinite(u.max) ? lvl + '/' + u.max : lvl + '/∞'}</span>
        </button>`;
    }).join('');

    // Detail card for the selected node.
    let detail = '<span class="hint" style="margin:0">Select a node to inspect it.</span>';
    if (selectedId) {
      const u = META_BY_ID[selectedId];
      const lvl = Save.metaLevel(u.id);
      const cost = nextCost(u);
      const unlocked = nodeUnlocked(u);
      const afford = cost !== null && d.coins >= cost;
      const reqs = u.requires.map(([id, rl]) => {
        const met = Save.metaLevel(id) >= rl;
        return `<span style="color:${met ? '#7dff9c' : 'var(--danger)'}">${met ? '✓' : '✗'} ${META_BY_ID[id].name} ${rl}</span>`;
      }).join(' &nbsp; ');
      detail = `
        <div class="sd-head"><span class="sd-icon">${u.icon}</span>
          <b>${u.name}</b> <span class="sd-lvl">${Number.isFinite(u.max) ? `Lv ${lvl}/${u.max}` : `Rank ${lvl} · no limit`}</span></div>
        <div class="sd-desc">${u.desc(Math.max(0, lvl - (lvl >= u.max ? 1 : 0)))}</div>
        ${reqs ? `<div class="sd-reqs">${reqs}</div>` : ''}
        <button class="btn ${afford && unlocked ? 'primary' : ''}" id="skill-buy"
                ${cost === null || !afford || !unlocked ? 'disabled' : ''}>
          ${cost === null ? '★ MAXED' : !unlocked ? '🔒 Locked' : '◈ ' + cost}
        </button>`;
    }

    const bonus = (n) => Math.round(n * 100) + '%';
    const ascCost = ascensionCost();
    const ascAfford = d.coins >= ascCost;
    const ascend = apexOwned ? `
      <div class="ascend-row">
        <span class="ascend-glyph" aria-hidden="true">☄</span>
        <div class="ascend-info"><b>ASCENSION READY</b> — the rite costs <b style="color:var(--gold)">◈ ${ascCost}</b>
          (each Ascension prices the next half again higher). Reset the tree (Endless
          Hunger stays); gain a permanent +${bonus(PRESTIGE_BONUS.damage)} damage,
          +${bonus(PRESTIGE_BONUS.xp)} XP and +${bonus(PRESTIGE_BONUS.gold)} coins per Ascension.</div>
        <button class="btn ${ascAfford ? 'primary' : ''}" id="btn-ascend" ${ascAfford ? '' : 'disabled'}>☄ ASCEND · ◈ ${ascCost}</button>
      </div>` : '';

    // BUY MAX — the post-Ascension rebuy sweep. The plan is the preview shown
    // on the button and in the confirm; buying itself walks the same
    // spendCoins/buyMeta path as clicking a node's own buy button.
    const plan = buyMaxPlan(d.coins);
    const treeMaxed = SKILL_TREE.every((u) => !Number.isFinite(u.max) || Save.metaLevel(u.id) >= u.max);
    const buyMaxHint = plan.ranks ? '' : `
      <p class="hint" id="buymax-hint" style="margin:4px 0 0">${treeMaxed
    ? '★ Every rank-limited node is maxed — only Endless Hunger is left to feed.'
    : 'BUY MAX sweeps the cheapest affordable ranks — nothing is affordable right now, go bank more coins.'}</p>`;

    // A pin-panel like the world map: the constellation (and the occasional
    // Ascension/support rows) scroll, the detail card and the BUY MAX / Back
    // row stay on screen at every viewport.
    this._show(`
      <div class="panel pin-panel tree-panel" style="max-width:760px">
        <div class="section-title">❖ THE FANG TREE</div>
        <div class="hint" style="margin-top:-6px">Banked coins buy permanent power. ◈ ${d.coins}
          ${prestige ? ` &nbsp;·&nbsp; <span style="color:var(--gold)">☄ Ascension ${prestige}</span>` : ''}</div>
        <div class="pin-scroll">
          <div class="tree-wrap">
            <svg class="tree-edges" viewBox="0 0 100 100" preserveAspectRatio="none">${lines}</svg>
            ${nodes}
          </div>
          ${ascend}
          <div id="iap-block"></div>
        </div>
        <div id="skill-detail">${detail}</div>
        <div class="btn-row">
          <button class="btn" id="btn-buymax" ${plan.ranks ? '' : 'disabled'}
                  title="Repeatedly buy the cheapest affordable rank until the bank runs dry (Endless Hunger excluded)">
            ⇈ BUY MAX${plan.ranks ? ` · ${plan.ranks} rank${plan.ranks === 1 ? '' : 's'} · ◈ ${plan.total}` : ''}</button>
          <button class="btn ghost" id="btn-back">◄ Back</button>
        </div>
        ${buyMaxHint}
      </div>`);

    this.root.querySelectorAll('.skill-node').forEach((el) => {
      el.addEventListener('click', () => { Audio.ui(); this.skillTree(opts, el.dataset.id); });
    });
    if (selectedId) {
      this._click('#skill-buy', () => {
        const u = META_BY_ID[selectedId];
        const cost = nextCost(u);
        if (cost !== null && nodeUnlocked(u) && Save.spendCoins(cost)) {
          Save.buyMeta(u.id);
          Audio.chest();
        } else Audio.hurt();
        this.skillTree(opts, selectedId);
      });
    }
    this._click('#btn-buymax', () => {
      const p = buyMaxPlan(Save.data.coins);
      if (!p.ranks) { Audio.hurt(); return; }
      if (!confirm(`BUY MAX?\n\nBuys the cheapest affordable unlocked rank, over and over, until the bank runs dry: ${p.ranks} rank${p.ranks === 1 ? '' : 's'} for ◈ ${p.total} (you have ◈ ${Save.data.coins}).\n\nEndless Hunger is never bought this way — feed it by hand.`)) return;
      let bought = 0;
      for (;;) {
        const pick = cheapestBuy(Save.data.coins);
        if (!pick) break;
        // The exact same purchase path as clicking a node's own buy button.
        if (!Save.spendCoins(pick.cost)) break;
        Save.buyMeta(pick.id);
        bought++;
      }
      if (bought) Audio.chest(); else Audio.hurt();
      this.skillTree(opts, selectedId);
    });
    this._click('#btn-ascend', () => {
      const cost = ascensionCost();
      if (!confirm(`ASCEND for ◈ ${cost}?\n\nThe Fang Tree resets to zero (Endless Hunger ranks are kept) — and you keep a permanent, stacking Ascension bonus forever. Coins spent on the tree are not refunded, and the next rite will cost half again more.`)) return;
      if (!Save.spendCoins(cost)) { Audio.hurt(); return; }
      ascendPreserving();
      Audio.multiply();
      if (onAscended) onAscended();
      this.skillTree(opts, null);
    });
    this._click('#btn-back', onBack);
    this._renderIap(opts);
  }

  // Real-money section — only appears when a native store is wired up.
  async _renderIap(opts) {
    if (!Platform.iap.available) return;
    const products = (await Platform.iap.products())
      .filter((p) => !(p.once && Save.ownsIap(p.sku)));
    const block = this.root.querySelector('#iap-block');
    if (!block) return;                          // user already left the shop
    block.innerHTML = `
      <div class="section-title" style="font-size:15px;margin-top:18px">✦ SUPPORT NEONFANG</div>
      <div class="shop-list">${products.map((p) => `
        <div class="shop-row">
          <div class="shop-icon">${p.icon}</div>
          <div class="shop-info">
            <div class="shop-name">${p.name}</div>
            <div class="shop-desc">${p.desc}</div>
          </div>
          <button class="btn shop-buy primary iap-buy" data-sku="${p.sku}">${p.price}</button>
        </div>`).join('')}
      </div>
      <div class="btn-row" style="margin-top:6px"><button class="btn ghost" id="btn-restore" style="font-size:12px;padding:6px 12px">Restore purchases</button></div>`;
    block.querySelectorAll('.iap-buy').forEach((btn) => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        const p = products.find((x) => x.sku === btn.dataset.sku);
        const { ok } = await Platform.iap.purchase(p.sku);
        if (ok) {
          if (p.coins) Save.addCoins(p.coins);
          if (p.once) Save.grantIap(p.sku);
          Audio.chest();
        } else { Audio.hurt(); btn.disabled = false; return; }
        this.skillTree(opts);
      });
    });
    this._click('#btn-restore', async () => {
      const { owned } = await Platform.iap.restore();
      for (const sku of owned) Save.grantIap(sku);
      Audio.chest();
      this.skillTree(opts);
    });
  }

  // --------------------------------------------------------------- settings
  // opts: { onBack, toast } — toast is main.js's pushToast (icon, html, cls),
  // passed in so this DOM layer never owns a notification channel of its own.
  settings(opts) {
    const { onBack } = opts;
    const toast = opts.toast || (() => {});
    const vol = Math.round(Settings.volume * 100);
    const toggle = (on) => on ? 'ON' : 'OFF';
    // A pin-panel: the option rows and backup tools scroll in the middle,
    // the heading and the Back button stay on screen at every viewport.
    this._show(`
      <div class="panel pin-panel" style="max-width:460px;width:min(460px,94vw)">
        <div class="section-title">⚙ SETTINGS</div>
        <div class="pin-scroll">
          <div class="set-row">
            <label>Volume</label>
            <input id="set-vol" type="range" min="0" max="100" value="${vol}" />
            <span id="set-vol-val">${vol}%</span>
          </div>
          <div class="set-row"><label>Mute</label><button class="btn set-toggle" id="set-mute">${toggle(Settings.muted)}</button></div>
          <div class="set-row"><label>Music</label><button class="btn set-toggle" id="set-music">${toggle(Settings.music)}</button></div>
          <div class="set-row"><label>Screen shake</label><button class="btn set-toggle" id="set-shake">${toggle(Settings.stored('shake'))}</button></div>
          <div class="set-row"><label>Damage numbers</label><button class="btn set-toggle" id="set-dmg">${toggle(Settings.damageNumbers)}</button></div>
          <div class="set-row"><label>Particles</label><button class="btn set-toggle" id="set-part">${Settings.particles === 'low' ? 'LOW' : 'HIGH'}</button></div>
          <div class="section-title" style="font-size:15px;margin-top:18px">💾 BACK UP YOUR SAVE</div>
          <div class="hint" style="text-align:left;margin:0 0 8px">
            Progress lives only on this device. Export a code and keep it
            somewhere safe — pasting it here later (or on another device)
            restores everything.
          </div>
          <div class="save-btns">
            <button class="btn" id="btn-export">⇪ Export</button>
            <button class="btn" id="btn-export-copy">⧉ Copy</button>
            <button class="btn" id="btn-export-dl">⤓ Download</button>
          </div>
          <textarea class="save-ta" id="export-code" readonly spellcheck="false"
            placeholder="Press Export to generate your save code." aria-label="Exported save code"></textarea>
          <textarea class="save-ta" id="import-code" spellcheck="false"
            placeholder="Paste a NEONFANG save code here to restore it…" aria-label="Save code to import"></textarea>
          <div class="save-btns">
            <button class="btn" id="btn-import">⇩ Import</button>
          </div>
        </div>
        <div class="btn-row"><button class="btn ghost" id="btn-back">◄ Back</button></div>
      </div>`);

    const volInput = this.root.querySelector('#set-vol');
    const volVal = this.root.querySelector('#set-vol-val');
    volInput.addEventListener('input', () => {
      const v = parseInt(volInput.value, 10);
      volVal.textContent = v + '%';
      Settings.volume = v / 100;
      Audio.setVolume(Settings.volume);
    });
    this._click('#set-mute', () => { Settings.muted = Settings.toggle('muted'); Audio.setMuted(Settings.muted); this.settings(opts); });
    this._click('#set-music', () => { const music = !Settings.toggle('musicOff'); Audio.setMusicEnabled(music); this.settings(opts); });
    this._click('#set-shake', () => { Settings.toggle('shake'); this.settings(opts); });
    this._click('#set-dmg', () => { Settings.toggle('damageNumbers'); this.settings(opts); });
    this._click('#set-part', () => { Settings.particles = Settings.particles === 'low' ? 'high' : 'low'; this.settings(opts); });
    this._click('#btn-back', onBack);

    // ---- save backup: export (copy / file) and import ----
    const exportTa = this.root.querySelector('#export-code');
    const importTa = this.root.querySelector('#import-code');
    const fillExport = () => { exportTa.value = Save.exportSave(); this._attachCues(); return exportTa.value; };
    this._click('#btn-export', () => { fillExport(); exportTa.focus(); exportTa.select(); });
    this._click('#btn-export-copy', async () => {
      const code = exportTa.value || fillExport();
      // navigator.clipboard needs a secure context and can be denied — fall
      // back to the selection-based path, and say so if even that fails.
      let ok = false;
      try { await navigator.clipboard.writeText(code); ok = true; } catch { /* fall through */ }
      if (!ok) {
        try { exportTa.focus(); exportTa.select(); ok = document.execCommand('copy'); } catch { ok = false; }
      }
      if (ok) toast('💾', '<b>Save code copied</b><br>Keep it somewhere safe.');
      else {
        exportTa.focus(); exportTa.select();
        toast('⚠️', '<b>Copy blocked</b><br>The code is selected — copy it by hand.', 'danger');
      }
    });
    this._click('#btn-export-dl', () => {
      // Clipboard fails on some mobile browsers; a plain text file always works.
      const code = exportTa.value || fillExport();
      const url = URL.createObjectURL(new Blob([code], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'neonfang-save.txt';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast('💾', '<b>Save downloaded</b><br>neonfang-save.txt');
    });
    this._click('#btn-import', () => {
      const check = decodeExport(importTa.value);           // validate before asking
      if (!check.ok) { toast('⚠️', `<b>Import failed</b><br>Nothing was changed — ${check.error}.`, 'danger'); return; }
      if (!confirm('Import this save?\n\nIt replaces your current progress (a backup of the current save is kept on this device). The game will reload.'
        + (check.future ? '\n\nNote: this code is from a newer version of the game — this version will load what it understands and keep the original safe.' : ''))) return;
      const res = Save.importSave(importTa.value);
      if (!res.ok) { toast('⚠️', `<b>Import failed</b><br>Nothing was changed — ${res.error}.`, 'danger'); return; }
      // Reload so the imported payload boots through the real load()/migrate()
      // chain — the flag lets boot() confirm success on the other side.
      try { sessionStorage.setItem('neonfang.import.ok', '1'); } catch { /* toast just won't show */ }
      location.reload();
    });
  }

  // ------------------------------------------------------------- achievements
  achievements({ onBack }) {
    const done = ACHIEVEMENTS.filter((a) => Save.isAchieved(a.id)).length;
    const cards = ACHIEVEMENTS.map((a) => {
      const got = Save.isAchieved(a.id);
      return `
        <div class="ach-row ${got ? 'got' : ''}">
          <div class="ach-icon">${got ? a.icon : '🔒'}</div>
          <div class="ach-info">
            <div class="ach-name">${a.name}${a.coins ? ` <span style="color:var(--gold);font-size:11px">◈${a.coins}</span>` : ''}</div>
            <div class="ach-desc">${got || !a.secret ? a.desc : '??? — a secret to discover'}</div>
          </div>
          <div class="ach-check">${got ? '✓' : ''}</div>
        </div>`;
    }).join('');
    this._show(`
      <div class="panel" style="max-width:600px">
        <div class="section-title">🏆 ACHIEVEMENTS</div>
        <div class="hint" style="margin-top:-6px">${done} / ${ACHIEVEMENTS.length} unlocked</div>
        <div class="ach-list">${cards}</div>
        <div class="btn-row"><button class="btn ghost" id="btn-back">◄ Back</button></div>
      </div>`);
    this._click('#btn-back', onBack);
  }

  // -------------------------------------------------------- character select
  characterSelect({ onPick, onBack }) {
    const cards = [...CharacterDefs.values()].map((c) => {
      const unlocked = Save.isUnlocked(c.id);
      const best = Save.data.best[c.id];
      const el = Elements.get(c.startElement);
      const locked = !unlocked;
      const canBuy = locked && Save.data.coins >= c.cost;
      // The .card-body wrapper is what lets narrow viewports lay the card out
      // as a horizontal row (same mechanism as the level-up cards) — the
      // signature blurbs made tall cards run one-per-screenful on phones.
      return `
        <div class="card ${locked ? 'locked' : ''}" data-id="${c.id}" data-locked="${locked}" data-cost="${c.cost}">
          <img class="card-icon" src="${spriteDataUri(c.sprite)}" alt="">
          <div class="card-body">
            <div class="card-name">${c.name}</div>
            <div class="card-tag" style="color:${el.color}">${el.short} ${c.title}</div>
            <div class="card-desc">${c.desc}</div>
            ${best ? `<div class="card-lvl">Best: ${fmtTime(best)}</div>` : ''}
            ${locked ? `<div class="card-lvl" style="color:${canBuy ? '#7dff9c' : '#ff6a80'}">🔒 ${c.cost} ◈ ${canBuy ? '(click to unlock)' : ''}</div>` : ''}
          </div>
        </div>`;
    }).join('');

    // A pin-panel: the roster scrolls, the Back button stays on screen — it
    // sat below the fold at BOTH audit viewports as a plain panel.
    this._show(`
      <div class="panel pin-panel">
        <div class="section-title">CHOOSE YOUR SURVIVOR</div>
        <div class="hint" style="margin-top:-6px">◈ ${Save.data.coins} coins available</div>
        <div class="pin-scroll"><div class="card-grid select-grid" style="margin-top:10px">${cards}</div></div>
        <div class="btn-row"><button class="btn ghost" id="btn-back">◄ Back</button></div>
      </div>`, { scenic: true });

    this.root.querySelectorAll('.card').forEach((cardEl) => {
      cardEl.addEventListener('click', () => {
        const id = cardEl.dataset.id;
        const def = CharacterDefs.get(id);
        const locked = cardEl.dataset.locked === 'true';
        if (locked) {
          const cost = parseInt(cardEl.dataset.cost, 10);
          if (Save.spendCoins(cost)) { Save.unlock(id); Audio.chest(); this.characterSelect({ onPick, onBack }); }
          else Audio.hurt();
          return;
        }
        Audio.ui();
        onPick(def);
      });
    });
    this._click('#btn-back', onBack);
  }

  // ----------------------------------------------------------- the world map
  // THE FANGLANDS — a hex overworld. Every hex is a run; clearing one conquers
  // it and opens its neighbours. Selecting a hex fills the detail card below.
  worldMap(opts, selectedId = null, tier = null) {
    const { onPick, onBack } = opts;
    const { pos, size, viewBox } = mapLayout(10, 3);
    const progress = mapProgress();

    // Pick a sensible default so the detail card is never empty.
    if (!selectedId || !nodeById(selectedId)) {
      const firstOpen = MAP_NODES.find((n) => nodeState(n) === 'available');
      selectedId = (firstOpen || MAP_NODES[0]).id;
      tier = null;
    }
    // Default to the tier you've already beaten here — the known-good setting,
    // one step below whatever you're allowed to push up to.
    const selNode = nodeById(selectedId);
    const selTier = clampThreat(tier === null ? Math.min(threatCeiling(), threatOf(selNode)) : tier);

    // Links between adjacent hexes — lit once both ends are yours.
    const links = mapLinks().map(([a, b]) => {
      const pa = pos[a.id], pb = pos[b.id];
      const both = isConquered(a) && isConquered(b);
      const one = isConquered(a) || isConquered(b);
      return `<line class="hx-link ${both ? 'lit' : one ? 'open' : ''}"
        x1="${pa.x.toFixed(2)}" y1="${pa.y.toFixed(2)}" x2="${pb.x.toFixed(2)}" y2="${pb.y.toFixed(2)}"
        vector-effect="non-scaling-stroke"/>`;
    }).join('');

    // Today's hex anomalies — rolled (and persisted) on the first map view of
    // a new UTC day; stored picks are canonical until UTC midnight.
    const anomalies = anomaliesForToday();
    const anomalyIds = new Set(anomalies.map((a) => a.nodeId));

    const hexes = MAP_NODES.map((n) => {
      const p = pos[n.id];
      const st = nodeState(n);
      const hidden = st === 'locked';
      const icon = hidden ? '·' : st === 'contested' ? '⚠' : isConquered(n) ? '✓' : n.type.icon;
      const anom = !hidden && anomalyIds.has(n.id);
      // Contested hexes get a hatched overlay on top of the red fill. Red is
      // colour-only and --danger simulates to the same khaki as the warm
      // region tints, so the texture is what separates "the horde holds this"
      // from "you hold this" for a red/green-blind player.
      const siege = st === 'contested';
      const label = (hidden ? 'Uncharted'
        : st === 'contested' ? `${n.name} — CONTESTED` : `${n.name} — ${n.type.name}`)
        + (anom ? ' — ANOMALY' : '');
      const mastery = hidden ? 0 : threatOf(n);
      // The ◬ badge sits above the icon; the threat numeral keeps its slot
      // below it — the two never collide.
      // Layered: base fill → region terrain pattern → siege hatch → badges.
      // The terrain overlay gives each region a texture signature (grass /
      // facets / embers / stars) so region identity is carried by pattern as
      // well as hue — Cavern and Void tints collapse under deuteranopia.
      // The anomaly ring is the only circle on the map: a shape no state
      // borrows, so ◬ hexes read at map scale without hunting.
      return `
        <g class="hexnode st-${st} rg-${n.regionId} ty-${n.typeId} ${n.id === selectedId ? 'sel' : ''}"
           data-id="${n.id}" role="button" tabindex="0" aria-label="${label}${mastery ? ` (Threat ${threatLabel(mastery)})` : ''}">
          <title>${label}${mastery ? ` · Threat ${threatLabel(mastery)}` : ''}</title>
          <polygon class="hx-base" points="${hexPoints(p.x, p.y, size * 0.92)}" vector-effect="non-scaling-stroke"/>
          <polygon class="hx-terrain" points="${hexPoints(p.x, p.y, size * 0.92)}" fill="url(#hxp-${n.regionId})"/>
          ${siege ? `<polygon class="hx-hatch" points="${hexPoints(p.x, p.y, size * 0.92)}"/>` : ''}
          ${anom ? `<circle class="hx-anom-ring" cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="${(size * 0.68).toFixed(2)}" vector-effect="non-scaling-stroke"/>` : ''}
          ${anom ? `<text class="hx-anom" x="${p.x.toFixed(2)}" y="${(p.y - 3.6).toFixed(2)}">◬</text>` : ''}
          <text class="hx-icon" x="${p.x.toFixed(2)}" y="${(p.y + (mastery ? 1.2 : 2.6)).toFixed(2)}">${icon}</text>
          ${mastery ? `<text class="hx-threat" x="${p.x.toFixed(2)}" y="${(p.y + 7).toFixed(2)}">${threatLabel(mastery)}</text>` : ''}
        </g>`;
    }).join('');

    // One glyph per region, so the key is not four coloured squares — Crystal
    // Cavern and The Void are the same blue to a deuteranope.
    const REGION_GLYPH = { meadow: '❀', cavern: '◈', wastes: '▲', void: '✧' };
    const legend = MAP_REGIONS.map((r) => {
      const pr = regionProgress(r.id);
      const done = pr.done >= pr.total;
      return `<span class="mp-region ${done ? 'done' : ''}" style="--rc:${r.color}">
        <i aria-hidden="true">${REGION_GLYPH[r.id] || '◆'}</i>${r.name} <b>${pr.done}/${pr.total}</b>${done ? ' ✓' : ''}${pr.contested ? ` <b class="mp-warn">⚠${pr.contested}</b>` : ''}</span>`;
    }).join('');

    // The reclamation banner: what you've lost, and when the horde moves next.
    const countdown = fmtCountdown(msToNextSiege());
    const siegeLine = progress.contested
      ? `<div class="map-siege">⚠ <b>${progress.contested} hex${progress.contested === 1 ? '' : 'es'} contested</b>
           — win there to drive the horde back off your land.
           ${progress.contested >= MAX_CONTESTED
    ? 'No more will fall until you do.'
    : countdown ? `Next incursion in <b>${countdown}</b>.` : ''}</div>`
      : countdown
        ? `<div class="map-siege calm">⚔ The horde tests your border again in <b>${countdown}</b>.</div>`
        : '';

    // Anomaly legend line — same countdown formatting as the siege clock.
    const anomalyLine = anomalies.length
      ? `<div class="map-anomaly">◬ <b>${anomalies.length}</b> anomal${anomalies.length === 1 ? 'y' : 'ies'} today
           — reset in <b>${fmtCountdown(msToAnomalyReset())}</b></div>`
      : '';

    // THE HUNT BOARD — the next three achievement bounties, straight off the
    // authored achievements list (nextHunts decides "next"; this layer names
    // no ids). It sits BELOW the map with the legend/anomaly context rows,
    // not above it with the alarm banners, so it can never squeeze the map
    // viewport under its ~200px floor. Re-rendered on every map visit, so an
    // achievement earned mid-session leaves the board the next time you look.
    const hunts = nextHunts((id) => Save.isAchieved(id));
    const huntBoard = hunts.length ? `
      <div class="map-hunts">
        <div class="mh-title">⚔ HUNTS <span class="mh-sub">— your next marks, paid in coin</span></div>
        ${hunts.map((h) => `
          <div class="hunt-row">
            <span class="hunt-icon">${h.icon}</span>
            <span class="hunt-name">${h.name}</span>
            <span class="hunt-goal">${h.desc}</span>
            <span class="hunt-coin">◈ ${h.coins}</span>
          </div>`).join('')}
      </div>` : '';

    // THE WEEKLY GAUNTLET — its own banner, not a hex. Opens with the first
    // fallen Lair; one attempt a week against the same seeded horde as
    // everyone else.
    let gauntletBanner = '';
    if (gauntletUnlocked()) {
      const g = Save.gauntletState();
      const gcfg = gauntletConfigFor();
      const spent = g.attempted && g.week === gcfg.gauntletWeek;
      const region = regionById(gcfg.stageId);
      gauntletBanner = `
        <div class="map-gauntlet">
          <span>⚔ <b>WEEKLY GAUNTLET</b> — ${region.name}, one attempt, every hunter
            fights the same horde.${g.clears ? ` <b class="gt-clears">⚔ ${g.clears} gauntlet${g.clears === 1 ? '' : 's'} survived</b>` : ''}${
  g.bestScore ? ` <b class="gt-clears">🏆 ${g.bestScore.toLocaleString('en-US')} best</b>` : ''}</span>
          <button class="btn ${spent ? '' : 'primary'}" id="btn-gauntlet" ${spent ? 'disabled' : ''}>
            ${spent ? (g.cleared ? '✓ Survived this week' : '✗ Spent this week') : '⚔ ENTER'}</button>
        </div>`;
    }

    this._show(`
      <div class="panel map-panel pin-panel" style="max-width:820px">
        <div class="section-title">🗺 THE FANGLANDS</div>
        <div class="hint" style="margin-top:-6px">
          Take a hex to conquer it and open its neighbours. ◈ ${Save.data.coins}
          &nbsp;·&nbsp; <b style="color:var(--accent)">${progress.done}/${progress.total}</b> hexes held
          ${Save.maxThreat() ? `&nbsp;·&nbsp; <b style="color:var(--danger)">☠ Threat ${threatLabel(Save.maxThreat())}</b> cleared` : ''}
        </div>
        <div class="pin-scroll">
          ${siegeLine}
          ${gauntletBanner}
          <div class="map-wrap">
            <svg class="hex-map" viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet">
              <defs>
                <pattern id="hx-siege" width="4" height="4" patternUnits="userSpaceOnUse"
                         patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="4" stroke="#ffe7ec" stroke-width="1.4" stroke-opacity="0.55"/>
                </pattern>
                <!-- Region terrain signatures, one pattern per biome. Shapes
                     are classed and coloured from styles.css (.hex-map .pt-*)
                     so the tints stay in the design system, not the markup. -->
                <pattern id="hxp-meadow" width="6.5" height="5.5" patternUnits="userSpaceOnUse">
                  <path class="pt-meadow" d="M1.1 4.7 Q1.3 3.7 1.0 3.1 M1.9 4.7 Q2.0 3.9 2.5 3.4
                                             M4.3 2.0 Q4.5 1.1 4.2 0.5 M5.1 2.0 Q5.2 1.3 5.7 0.8"/>
                </pattern>
                <pattern id="hxp-cavern" width="7.5" height="6.5" patternUnits="userSpaceOnUse">
                  <path class="pt-cavern" d="M1.9 0.9 L3.0 2.4 L1.9 3.9 L0.8 2.4 Z M1.9 0.9 L1.9 3.9"/>
                  <path class="pt-cavern" d="M5.7 3.4 L6.5 4.5 L5.7 5.6 L4.9 4.5 Z"/>
                </pattern>
                <pattern id="hxp-wastes" width="7.5" height="6.5" patternUnits="userSpaceOnUse">
                  <circle class="pt-wastes-f" cx="1.5" cy="1.3" r="0.45"/>
                  <circle class="pt-wastes-f" cx="5.3" cy="4.7" r="0.32"/>
                  <path class="pt-wastes-f" d="M3.4 5.4 L4.2 5.4 L3.8 4.6 Z"/>
                  <circle class="pt-ember-f" cx="6.3" cy="1.6" r="0.26"/>
                </pattern>
                <pattern id="hxp-void" width="8.5" height="7.5" patternUnits="userSpaceOnUse">
                  <circle class="pt-void-f" cx="1.3" cy="1.1" r="0.3"/>
                  <circle class="pt-void-f" cx="4.4" cy="5.2" r="0.22"/>
                  <circle class="pt-void-f" cx="6.9" cy="2.4" r="0.34"/>
                  <circle class="pt-void-f" cx="6.1" cy="6.6" r="0.18"/>
                  <path class="pt-void" d="M3.1 2.6 L3.1 4.0 M2.4 3.3 L3.8 3.3"/>
                </pattern>
              </defs>
              <g class="hx-links">${links}</g>${hexes}
            </svg>
          </div>
          <div class="map-legend">${legend}</div>
          ${anomalyLine}
          ${huntBoard}
        </div>
        <div id="map-detail">${this._nodeDetail(selNode, selTier)}</div>
        <div class="btn-row">
          <button class="btn ghost" id="btn-shrines">✶ Blessings</button>
          <button class="btn ghost" id="btn-bestiary">📖 Bestiary</button>
          <button class="btn ghost" id="btn-back">◄ Back</button>
        </div>
      </div>`, { scenic: true });

    this.root.querySelectorAll('.hexnode').forEach((el) => {
      el.addEventListener('click', () => { Audio.ui(); this.worldMap(opts, el.dataset.id); });
    });
    this._click('#threat-down', () => this.worldMap(opts, selectedId, selTier - 1));
    this._click('#threat-up', () => this.worldMap(opts, selectedId, selTier + 1));
    this._click('#btn-march', () => {
      if (selNode && nodeState(selNode) !== 'locked' && !gateReason(selNode)) onPick(selNode, selTier);
      else Audio.hurt();
    });
    this._click('#btn-shrines', () => this.blessings({ onBack: () => this.worldMap(opts, selectedId, selTier) }));
    this._click('#btn-bestiary', () => this.bestiary({ onBack: () => this.worldMap(opts, selectedId, selTier) }));
    this._click('#btn-gauntlet', () => { if (opts.onGauntlet) opts.onGauntlet(); });
    this._click('#btn-back', onBack);
  }

  // Detail card for the selected hex: what it is, what it costs you, and the
  // button that starts the run (or the rule keeping you out).
  _nodeDetail(node, tier = 0) {
    if (!node) return '<span class="hint" style="margin:0">Select a hex.</span>';
    const st = nodeState(node);
    const region = regionById(node.regionId);
    const stage = getStage(node.stageId);
    if (st === 'locked') {
      return `
        <div class="md-head"><span class="md-icon">🌫</span><b>Uncharted</b></div>
        <div class="md-desc">Territory you haven't reached. Conquer your way toward it.</div>`;
    }
    // A contested hex is dug in — the run config carries the retake penalty
    // and payout (anomaly premium included), so read the real numbers off it
    // rather than the node type.
    const cfg = runConfigFor(node, tier);
    const anom = anomalyFor(node);
    const mins = Math.floor(cfg.duration / 60);
    const secs = String(cfg.duration % 60).padStart(2, '0');
    const threat = (stage.difficulty || { hp: 1 }).hp * cfg.diff * cfg.threatHp;
    const gate = gateReason(node);
    const held = isHeld(node);
    const lost = isContested(node);
    const best = threatOf(node);
    const ceiling = threatCeiling();
    const payout = (lost ? reclaimBounty(node) : held ? 0 : node.type.bounty)
      + (cfg.threat > best ? threatBounty(node, cfg.threat) : 0);
    // Threat picker — only offered where you can actually march.
    const threatRow = gate ? '' : `
      <div class="md-threat">
        <button class="btn ghost tbtn" id="threat-down" ${cfg.threat <= 0 ? 'disabled' : ''}>◄</button>
        <span class="md-threat-lbl">THREAT <b>${cfg.threat ? threatLabel(cfg.threat) : '—'}</b></span>
        <button class="btn ghost tbtn" id="threat-up" ${cfg.threat >= ceiling ? 'disabled' : ''}>►</button>
        <span class="md-threat-note">${cfg.threat >= ceiling
    ? (ceiling >= MAX_THREAT ? 'the deepest tier there is' : 'clear this to unlock the next tier')
    : best ? `best here: ${threatLabel(best)}` : 'raise it for a deadlier horde and a bigger purse'}</span>
      </div>`;
    const btn = gate
      ? `<button class="btn" id="btn-march" disabled>🔒 ${gate}</button>`
      : lost
        ? '<button class="btn primary danger" id="btn-march">⚔ RECLAIM</button>'
        : `<button class="btn primary" id="btn-march">${held ? '↻ March again' : '▶ MARCH'}</button>`;
    // The content scrolls INSIDE .md-scroll; the march button is a sibling
    // outside it, so it can never float over the threat stepper or boon rows
    // the way the old sticky-inside-the-scroller shape did.
    return `
      <div class="md-scroll">
      <div class="md-head">
        <span class="md-icon">${lost ? '⚠' : node.type.icon}</span>
        <b>${node.name}</b>
        <span class="md-type">${node.type.name}</span>
        <span class="md-region" style="color:${region.color}">${region.name}</span>
        ${held ? '<span class="md-held">✓ HELD</span>' : ''}
        ${lost ? '<span class="md-lost">⚠ CONTESTED</span>' : ''}
        ${best ? `<span class="md-mastery">☠ ${threatLabel(best)}</span>` : ''}
        ${Save.overrunBest(node.id) ? `<span class="md-mastery" style="color:#b06bff">🌀 +${fmtTime(Save.overrunBest(node.id))}</span>` : ''}
      </div>
      <div class="md-desc">${lost
    ? 'The horde has taken this ground back and dug in. Win here to drive them off it.'
    : (node.blurb || node.type.blurb)}</div>
      ${anom ? `<div class="md-anom">◬ <b>ANOMALY — ${anom.name.toUpperCase()}</b> · ${anom.blurb}
        · payout ×${anom.payout.toFixed(2)} today</div>` : ''}
      <div class="md-stats">
        <span>⏱ <b>${mins}:${secs}</b></span>
        <span>☠ threat <b>×${threat.toFixed(2)}</b></span>
        <span title="Expected coins for a full run, against a Skirmish in this region">◈ payout <b>×${cfg.payout.toFixed(2)}</b></span>
        ${payout ? `<span class="md-bounty">${lost ? 'reclaim' : cfg.threat > best && held ? 'mastery' : 'first clear'} <b>◈ ${payout}</b></span>` : ''}
      </div>
      ${threatRow}
      <div class="md-loadout">⚑ <b>Territory boon</b> — start with <b>${cfg.weaponName}</b> · ${cfg.buffText}</div>
      ${node.boonText ? `<div class="md-boon ${lost ? 'dark' : ''}">✶ ${node.boonText}${
  lost ? ' <b>— suspended while contested</b>' : held ? ' <b>— claimed</b>' : ''}</div>` : ''}
      </div>
      ${btn}`;
  }

  // ---------------------------------------------------------------- bestiary
  // Every foe in the registries, revealed by killing one. Undiscovered rows
  // show a blacked-out silhouette — the roster's shape is visible, the
  // specifics are earned. Sourced straight from EnemyDefs/BossDefs, so new
  // content appears here with zero extra wiring.
  bestiary({ onBack }) {
    const seen = new Set(Save.data.seen);
    const row = (key, d) => {
      const got = seen.has(key);
      return `
        <div class="ach-row ${got ? 'got' : ''}">
          <img class="codex-icon ${got ? '' : 'codex-hidden'}" src="${spriteDataUri(d.sprite)}" alt="">
          <div class="ach-info">
            <div class="ach-name">${got ? (d.name || d.id) : '???'}</div>
            <div class="ach-desc">${got ? (d.blurb || '') : 'Undiscovered — fell one to record it.'}</div>
          </div>
          <div class="ach-check">${got ? '✓' : ''}</div>
        </div>`;
    };
    const enemies = [...EnemyDefs.values()];
    const bosses = [...BossDefs.values()];
    const found = enemies.filter((d) => seen.has('e:' + d.id)).length
      + bosses.filter((d) => seen.has('b:' + d.id)).length;
    this._show(`
      <div class="panel pin-panel" style="max-width:600px">
        <div class="section-title">📖 BESTIARY</div>
        <div class="hint" style="margin-top:-6px">${found} / ${enemies.length + bosses.length} recorded — know your enemy.</div>
        <div class="pin-scroll">
          <div class="ach-list" style="max-height:none;overflow:visible">
            ${enemies.map((d) => row('e:' + d.id, d)).join('')}
            <div class="section-title" style="font-size:15px;margin-top:14px">👑 BOSSES</div>
            ${bosses.map((d) => row('b:' + d.id, d)).join('')}
          </div>
        </div>
        <div class="btn-row"><button class="btn ghost" id="btn-back">◄ Back</button></div>
      </div>`);
    this._click('#btn-back', onBack);
  }

  // Every shrine on the map and whether its blessing is yours.
  blessings({ onBack }) {
    const rows = shrineStatus().map((s) => `
      <div class="ach-row ${s.claimed ? 'got' : ''}">
        <div class="ach-icon">${s.claimed ? '✶' : s.contested ? '⚠' : '🔒'}</div>
        <div class="ach-info">
          <div class="ach-name">${s.name} <span style="color:var(--muted);font-size:11px">${s.region}</span></div>
          <div class="ach-desc">${s.text}${s.contested ? ' <b style="color:var(--danger)">— suspended, hex contested</b>' : ''}</div>
        </div>
        <div class="ach-check">${s.claimed ? '✓' : ''}</div>
      </div>`).join('');
    this._show(`
      <div class="panel" style="max-width:560px">
        <div class="section-title">✶ BLESSINGS</div>
        <div class="hint" style="margin-top:-6px">Shrine hexes grant permanent bonuses to every run.</div>
        <div class="ach-list">${rows}</div>
        <div class="btn-row"><button class="btn ghost" id="btn-back">◄ Back</button></div>
      </div>`);
    this._click('#btn-back', onBack);
  }

  // --------------------------------------------------------------- level up
  levelUp({ level, cards, onChoose, onReroll, onSkip, onBanish, rerolls, banishes }) {
    let banishMode = false;
    // The icon + a .card-body wrapper (rather than loose siblings) is what lets
    // narrow viewports lay the card out as a horizontal row — all three choices
    // on screen at once instead of two below the fold (see .levelup-grid).
    const cardHtml = cards.map((c, i) => `
      <div class="card rarity-${c.rarity}" data-i="${i}">
        <div class="card-key">${i + 1}</div>
        <div class="card-icon-glyph">${c.icon}</div>
        <div class="card-body">
          <div class="card-tag ${c.tagClass}">${c.tag}</div>
          <div class="card-name">${c.title}</div>
          <div class="card-lvl">${c.sub || ''}</div>
          <div class="card-desc">${typeof c.desc === 'function' ? c.desc() : c.desc}</div>
        </div>
      </div>`).join('');
    this._show(`
      <div class="panel pin-panel" style="text-align:center">
        <div class="section-title">LEVEL ${level} — CHOOSE AN UPGRADE</div>
        <div class="pin-scroll">
          <div class="card-grid levelup-grid">${cardHtml}</div>
        </div>
        <div class="btn-row" id="lvl-actions">
          <button class="btn ghost" id="btn-reroll" ${rerolls <= 0 ? 'disabled' : ''}>↻ Reroll (${rerolls})</button>
          <button class="btn ghost" id="btn-banish" ${banishes <= 0 ? 'disabled' : ''}>⌦ Banish (${banishes})</button>
          <button class="btn ghost" id="btn-skip">Skip ▸ +heal</button>
        </div>
        <p class="hint" id="lvl-hint">Pick a card to grow your build.</p>
      </div>`);

    const hint = this.root.querySelector('#lvl-hint');
    this.root.querySelectorAll('.card').forEach((el) => {
      el.addEventListener('click', () => {
        const card = cards[parseInt(el.dataset.i, 10)];
        Audio.ui();
        if (banishMode) onBanish(card);
        else onChoose(card);
      });
    });
    this._click('#btn-reroll', () => { if (rerolls > 0) onReroll(); });
    this._click('#btn-skip', () => onSkip());
    const banishBtn = this.root.querySelector('#btn-banish');
    if (banishBtn) banishBtn.addEventListener('click', () => {
      if (banishes <= 0) return;
      Audio.ui();
      banishMode = !banishMode;
      banishBtn.classList.toggle('primary', banishMode);
      hint.textContent = banishMode ? 'Click a card to BANISH it from this run.' : 'Pick a card to grow your build.';
    });
  }

  // ---------------------------------------------- chest / ultraweapon reveal
  chest({ evolved, onClose, heading = '✦ TREASURE ✦', tag = 'WEAPON EVOLVED' }) {
    this._show(`
      <div class="panel" style="text-align:center;max-width:420px">
        <div class="section-title" style="color:var(--gold)">${heading}</div>
        <div class="chest-pop" style="font-size:64px;line-height:1;margin:10px 0">${evolved.icon}</div>
        <div class="card-tag tag-evolution" style="font-size:12px">${tag}</div>
        <div class="card-name" style="font-size:22px;margin-top:4px">${evolved.name}</div>
        <div class="card-desc" style="max-width:320px;margin:10px auto 0">${evolved.desc}</div>
        <div class="btn-row"><button class="btn primary" id="btn-claim">Claim</button></div>
      </div>`);
    this._click('#btn-claim', onClose);
  }

  // ------------------------------------------------------------ overrun offer
  // The finale is beaten and the win is already banked — stay for the endless
  // phase? Falling during OVERRUN still ends the run as the win it is.
  overrunOffer({ onStay, onBank, bestSeconds }) {
    this._show(`
      <div class="panel" style="text-align:center;max-width:460px">
        <div class="title" style="font-size:32px">HEX CLEARED</div>
        <div class="subtitle" style="color:var(--gold)">the win is banked — but the horde does not relent</div>
        <div class="fang-divider"></div>
        <p class="hint" style="margin-top:0">
          Stay for <b style="color:#b06bff">🌀 OVERRUN</b>: endless waves that grow crueller by the
          minute and a purse that grows with them. Falling keeps the win and
          everything you've earned${bestSeconds ? ` — your record here is <b style="color:#b06bff">+${fmtTime(bestSeconds)}</b>` : ''}.
        </p>
        <div class="btn-row" style="flex-direction:column;align-items:center">
          <button class="btn primary" id="btn-overrun-stay" style="width:280px">🌀 STAY — OVERRUN</button>
          <button class="btn" id="btn-overrun-bank" style="width:280px">✓ Bank the win</button>
        </div>
      </div>`);
    this._click('#btn-overrun-stay', onStay);
    this._click('#btn-overrun-bank', onBank);
  }

  // ------------------------------------------------------------ revive offer
  // Shown when the player dies with a rewarded ad available (native builds).
  reviveOffer({ onWatch, onDecline }) {
    this._show(`
      <div class="panel" style="text-align:center;max-width:420px">
        <div class="title" style="font-size:30px">DOWN — NOT OUT</div>
        <p class="hint">Watch a short ad to get back up with half health<br>(once per run — enemies nearby are obliterated).</p>
        <div class="btn-row" style="flex-direction:column;align-items:center">
          <button class="btn primary" id="btn-revive" style="width:260px">▶ Watch ad · Revive</button>
          <button class="btn ghost" id="btn-decline" style="width:260px">Accept your fate</button>
        </div>
      </div>`);
    this._click('#btn-revive', onWatch);
    this._click('#btn-decline', onDecline);
  }

  // ----------------------------------------------------------------- pause
  pause({ onResume, onSettings, onMenu, buildInfo, damageInfo, stats }) {
    const chips = (buildInfo || []).map((b) =>
      `<span class="weapon-chip ${b.cls || ''}"><span style="font-size:14px">${b.icon}</span>${b.lvl ? `<span class="lvl">${b.lvl}</span>` : ''}</span>`).join('');
    const statHtml = (stats || []).map(([k, v]) =>
      `<span class="k">${k}</span><span class="v">${v}</span>`).join('');
    this._show(`
      <div class="panel" style="text-align:center;max-width:520px">
        <div class="section-title">PAUSED</div>
        ${chips ? `<div class="hint" style="margin-bottom:4px">Your build</div><div style="display:flex;flex-wrap:wrap;gap:6px;justify-content:center;margin-bottom:12px">${chips}</div>` : ''}
        ${damageInfo && damageInfo.length ? `<div class="hint" style="margin-bottom:4px">Damage dealt</div>${dmgRows(damageInfo)}` : ''}
        ${statHtml ? `<details class="pause-stats"><summary>Character stats</summary>
          <div class="summary-grid" style="font-size:12px;margin:10px auto;max-width:360px">${statHtml}</div></details>` : ''}
        <div class="btn-row" style="flex-direction:column;align-items:center">
          <button class="btn primary" id="btn-resume" style="width:240px">▶ Resume</button>
          <button class="btn" id="btn-settings" style="width:240px">⚙ Settings</button>
          <button class="btn ghost" id="btn-menu" style="width:240px">Quit to Menu</button>
        </div>
      </div>`);
    this._click('#btn-resume', onResume);
    this._click('#btn-settings', onSettings);
    this._click('#btn-menu', onMenu);
  }

  // -------------------------------------------------------------- game over
  gameOver({ summary, coinsEarned, bonus, bounty, bountyLabel, retryLabel, onRetry, onMenu, onMap, unlocks, isBest, canDouble, onDouble, score, scoreBest, shareText, overrunBest }) {
    const el = Elements.get(summary.element);
    const won = summary.won;
    const unlockHtml = unlocks && unlocks.length
      ? `<p class="hint" style="color:#7dff9c">✦ Unlocked: ${unlocks.join(', ')}</p>` : '';
    const clearedLabel = summary.nodeId ? '★ HEX CONQUERED ★' : '★ STAGE CLEARED ★';
    const titleHtml = won
      ? `<div class="title" style="font-size:34px">SURVIVED</div><div class="subtitle" style="color:var(--gold)">${clearedLabel}</div>`
      : `<div class="title" style="font-size:34px">YOU DIED</div>${isBest ? '<div class="subtitle" style="color:var(--gold)">★ NEW BEST TIME ★</div>' : ''}`;
    this._show(`
      <div class="panel" style="text-align:center;max-width:460px">
        ${titleHtml}
        <div class="fang-divider"></div>
        <div class="summary-grid" style="max-width:340px;margin:18px auto">
          <span class="k">${won ? 'Cleared in' : 'Survived'}</span><span class="v">${fmtTime(summary.seconds)}</span>
          <span class="k">${summary.nodeId ? 'Hex' : 'Stage'}</span><span class="v">${summary.nodeName || summary.stage || '—'}</span>
          ${summary.nodeId ? `<span class="k">Region</span><span class="v">${summary.stage}</span>` : ''}
          ${summary.threat ? `<span class="k">Threat</span><span class="v" style="color:var(--danger)">☠ ${threatLabel(summary.threat)}</span>` : ''}
          <span class="k">Level</span><span class="v">${summary.level}</span>
          <span class="k">Kills</span><span class="v">${summary.kills}</span>
          <span class="k">Best Combo</span><span class="v">${summary.bestCombo || 0}</span>
          <span class="k">Element</span><span class="v" style="color:${el.color}">${el.short} ${el.name}</span>
          ${summary.seed != null ? `<span class="k">Seed</span><span class="v" style="font-size:12px">${formatSeed(summary.seed)}${
  summary.gauntlet ? ' <span style="color:var(--muted)">— this week’s shared horde</span>' : ''}</span>` : ''}
          ${score != null ? `<span class="k">Score</span><span class="v" style="color:var(--gold)">🏆 ${score.toLocaleString('en-US')}${scoreBest ? ' <span style="font-size:11px">★ personal best</span>' : ''}</span>` : ''}
          ${summary.overrun ? `<span class="k">Overrun</span><span class="v" style="color:#b06bff">🌀 +${fmtTime(summary.overrunSeconds)}${overrunBest ? ' <span style="font-size:11px">★ hex record</span>' : ''}</span>` : ''}
          ${bonus ? `<span class="k">Clear Bonus</span><span class="v" style="color:#7dff9c">◈ +${bonus}</span>` : ''}
          ${bounty ? `<span class="k">${bountyLabel || 'First Clear'}</span><span class="v" style="color:var(--gold)">◈ +${bounty}</span>` : ''}
          <span class="k">Coins Earned</span><span class="v" id="coins-earned" style="color:#ffd15c">◈ ${coinsEarned}</span>
        </div>
        ${topWeaponsHtml(summary)}
        ${unlockHtml}
        ${canDouble ? '<div class="btn-row"><button class="btn" id="btn-double" style="border-color:var(--gold);color:var(--gold)">▶ Watch ad · Double coins</button></div>' : ''}
        <div class="btn-row">
          <button class="btn primary" id="btn-retry">${retryLabel || `↻ ${won ? 'Play Again' : 'Retry'}`}</button>
          ${shareText ? '<button class="btn" id="btn-share">⧉ Share</button>' : ''}
          ${onMap ? '<button class="btn" id="btn-map">🗺 World Map</button>' : ''}
          <button class="btn ghost" id="btn-menu">Menu</button>
        </div>
      </div>`);
    this._click('#btn-retry', onRetry);
    if (onMap) this._click('#btn-map', onMap);
    this._click('#btn-menu', onMenu);
    if (canDouble) this._click('#btn-double', onDouble);
    if (shareText) this._click('#btn-share', async () => {
      // Same clipboard strategy as the save-code copy: try the async API,
      // fall back to a selected hidden textarea, and say which happened.
      const btn = this.root.querySelector('#btn-share');
      let ok = false;
      try { await navigator.clipboard.writeText(shareText); ok = true; } catch { /* fall through */ }
      if (!ok) {
        try {
          const ta = document.createElement('textarea');
          ta.value = shareText;
          this.root.appendChild(ta);
          ta.select(); ok = document.execCommand('copy');
          ta.remove();
        } catch { ok = false; }
      }
      if (btn) btn.textContent = ok ? '✓ Copied' : '✗ Copy blocked';
    });
  }

  // Rewarded coin-doubler succeeded — update the results panel in place.
  markDoubled(total) {
    const v = this.root.querySelector('#coins-earned');
    if (v) v.textContent = `◈ ${total} (×2!)`;
    const btn = this.root.querySelector('#btn-double');
    if (btn) { btn.disabled = true; btn.textContent = '✓ Doubled'; }
  }
}
